import { UsersRound } from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { useSchedulingTeam } from '@/hooks/scheduling';
import { formatDuration } from '@/lib/dates';
import { cn } from '@/lib/utils';
import { EmployeeSchedulePane } from '@/pages/admin/scheduling/EmployeeSchedulePane';
import type { SchedulingTeamMember } from '@shared/schemas/availability';

/*
 * /admin/scheduling — ROADMAP.md §7.2.
 *
 * **The prototype's strongest single page, rebuilt close to as-is** (FINDINGS.md
 * §2). Team members on the left, weekly template on the right, one toggle per
 * day, per-day break minutes, a computed hours badge per row, a running
 * `5 days · 42h 30m/week` total in the header, five quick presets underneath,
 * and tabs splitting Weekly Schedule from Date Overrides. The layout is
 * preserved deliberately: the people who will use this have been using it, and
 * reorganising it is a change to their day rather than an improvement to the
 * code.
 *
 * **This screen is the one that makes anybody assignable.** Availability
 * resolves override → weekly → *unavailable* (business-rules.md §4), so a
 * cleaner with nothing here is disqualified from the automatic ranking and
 * carries a warning on a manual assignment. The left column says "No hours set"
 * rather than showing a blank, because that is the actionable fact.
 *
 * Desktop-first and dense, which is what this screen is for. The controls still
 * pass 44×44 wherever they are reachable on a phone — the e2e sweep measures
 * rendered boxes at 375px and a 43px pill has already been caught once.
 */

export function SchedulingPage() {
  const team = useSchedulingTeam();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const members = team.data?.data ?? [];
  const selected = members.find((member) => member.employeeId === selectedId) ?? members[0];

  return (
    <AdminPage
      title="Scheduling"
      description="Who works which days, and the one-offs on top — time off, cover, a different finish."
    >
      {team.isError ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="The team could not be loaded"
            onRetry={() => {
              void team.refetch();
            }}
          />
        </div>
      ) : team.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading the team</span>
          <div aria-hidden="true" className="h-96 animate-pulse rounded-lg bg-white" />
        </div>
      ) : members.length === 0 ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={UsersRound}
            title="Nobody to schedule yet."
            hint="Add a cleaner under People → Employees and their weekly schedule appears here."
          />
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
          <TeamList
            members={members}
            selectedId={selected?.employeeId ?? null}
            onSelect={setSelectedId}
          />

          {selected ? (
            // Keyed on the person, so selecting somebody else is a clean slate:
            // no unsaved Monday can survive the change and be saved onto the
            // wrong cleaner.
            <EmployeeSchedulePane key={selected.employeeId} member={selected} />
          ) : null}
        </div>
      )}
    </AdminPage>
  );
}

/** The left column: everybody, with how much of a week they are down for. */
function TeamList({
  members,
  selectedId,
  onSelect,
}: {
  members: SchedulingTeamMember[];
  selectedId: string | null;
  onSelect: (employeeId: string) => void;
}) {
  return (
    <nav
      aria-label="Team"
      className="h-fit overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card"
    >
      <ul className="divide-y divide-ink-100">
        {members.map((member) => {
          const selected = member.employeeId === selectedId;

          return (
            <li key={member.employeeId}>
              <button
                type="button"
                aria-current={selected ? 'true' : undefined}
                onClick={() => {
                  onSelect(member.employeeId);
                }}
                className={cn(
                  'flex min-h-touch w-full flex-col items-start gap-0.5 px-4 py-3 text-left',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-honey-700',
                  selected ? 'bg-honey-50' : 'hover:bg-paper-50',
                )}
              >
                <span className="text-body font-medium text-ink-900">{member.name}</span>
                <span className="text-meta text-ink-500">
                  {/*
                    Zero is `0` (P1) — and "No hours set" is the reading that
                    matters most, because unavailable is the default: that person
                    cannot be auto-assigned to anything until this is filled in.
                  */}
                  {member.daysWorking === 0
                    ? 'No hours set'
                    : `${member.daysWorking} day${member.daysWorking === 1 ? '' : 's'} · ${formatDuration(member.weeklyMinutes)}/week`}
                </span>
                {member.upcomingOverrides > 0 ? (
                  <span className="text-meta text-bark-700">
                    {member.upcomingOverrides} upcoming override
                    {member.upcomingOverrides === 1 ? '' : 's'}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
